import type { SearchLegRequest, SearchRequest, WidenKeys } from '@optionway/models';
import { modelsImpl } from '@optionway/models';

type FilteringCriteriaKey = {
  [K in keyof Pick<SearchRequest, 'legs'>]: Omit<SearchRequest[K][number], 'departureDate' | 'from' | 'to'>[];
};

interface SearchResultFiltersStore {
  // Managed by ResultsList: set from shouldActivate (debounce callback),
  // reset by the settlement watch after the BFF call completes, or by resetFilters.
  // staleTime: Infinity on the query prevents a second call when the cached key is unchanged.
  active: boolean;
  // Snapshot of the filtering criteria taken when no request is in flight.
  // Starts as { legs: [] } (no legs = not yet committed) — enabled guard checks legs.length > 0
  // so no premature fetch fires before the first debounce. Same concept as searchedCriteriaKey.
  searchedFilteringCriteriaKey: FilteringCriteriaKey;
  // Set by entry-point middleware when filter criteria were pre-filled from URL query params.
  // list.vue mainStatus watch reads it once the main query settles, resets it, then triggers
  // the filtering query if needed — the debounced watcher never fired for pre-filled filters.
  fromDeepLink: boolean;
}

export const useSearchResultFiltersStore = defineStore('search-result-filters', {
  state: (): SearchResultFiltersStore => ({
    active: false,
    searchedFilteringCriteriaKey: { legs: [] },
    fromDeepLink: false,
  }),
  getters: {
    filteredSearchRequest(): SearchRequest {
      const criteria = useSearchCriteriaStore();
      const stops = useStopsFilterStore();
      const hours = useHoursFilterStore();
      const duration = useDurationFilterStore();
      const airports = useAirportsFilterStore();
      const airlines = useAirlinesFilterStore();

      // Committed criteria, never the live form: filtering refines the search that was actually run.
      // The form would send an uncommitted pax count, answered under the committed key — fares for
      // criteria the user never validated.
      const committed = criteria.committedCriteriaKey;

      return {
        tripType: committed.tripType,
        adults: committed.adults,
        cabinClass: committed.cabinClass,
        children: committed.children,
        infants: committed.infants,
        // sessionData is GDS session state, not a criterion — it has no committed counterpart.
        sessionData: criteria.searchRequest.sessionData,
        legs: committed.legs.map((l, index) => {
          const leg: SearchLegRequest = {
            from: l.from,
            to: l.to,
            departureDate: l.departureDate,
            maxStop: stops.maxStop,
            maxConnectionTime: stops.maxConnectionTime,
            minConnectionTime: stops.minConnectionTime,
            disableMultiAirportConnection: stops.disableMultiAirportConnection,
            earliestDepartureTime: hours.earliestDepartureTimeByLeg[index],
            latestDepartureTime: hours.latestDepartureTimeByLeg[index],
            maxTravelTime: duration.maxTravelTimeByLeg[index],
            allowedDepartureAirports: airports.allowedDepartureAirports,
            allowedArrivalAirports: airports.allowedArrivalAirports,
            allowedAirlines: airlines.allowedAirlines,
          };
          return leg;
        }),
      };
    },
    toBody() {
      const searchObject = new modelsImpl.SearchRequestImpl() as WidenKeys<SearchRequest>;
      for (const key of Object.keys(searchObject) as (keyof SearchRequest)[]) {
        searchObject[key] = toRaw(this.filteredSearchRequest[key]);
      }
      return toPojo(searchObject);
    },
    activeFiltersCount(): number {
      return this.activeFilterTypes.length;
    },
    activeFilterTypes(): string[] {
      const filters: [string, { hasActiveFilters: boolean }][] = [
        ['stops', useStopsFilterStore()],
        ['hours', useHoursFilterStore()],
        ['duration', useDurationFilterStore()],
        ['airports', useAirportsFilterStore()],
        ['airlines', useAirlinesFilterStore()],
      ];
      return filters.filter(([, store]) => store.hasActiveFilters).map(([type]) => type);
    },
    isImpossible(): boolean {
      return (
        useStopsFilterStore().isImpossible ||
        useHoursFilterStore().isImpossible ||
        useDurationFilterStore().isImpossible ||
        useAirportsFilterStore().isImpossible ||
        useAirlinesFilterStore().isImpossible
      );
    },
    filterQueryString(): string {
      const stops = useStopsFilterStore();
      const duration = useDurationFilterStore();
      const hours = useHoursFilterStore();
      const airports = useAirportsFilterStore();
      const airlines = useAirlinesFilterStore();
      const parameters = new URLSearchParams();
      for (const [key, value] of new URLSearchParams(stops.filterQueryString)) parameters.append(key, value);
      for (const [key, value] of new URLSearchParams(duration.filterQueryString)) parameters.append(key, value);
      for (const [key, value] of new URLSearchParams(hours.filterQueryString)) parameters.append(key, value);
      for (const [key, value] of new URLSearchParams(airports.filterQueryString)) parameters.append(key, value);
      for (const [key, value] of new URLSearchParams(airlines.filterQueryString)) parameters.append(key, value);
      return parameters.toString().toLowerCase();
    },
    filteringCriteriaKey(): FilteringCriteriaKey {
      return {
        legs: this.filteredSearchRequest.legs.map((l) => ({
          maxStop: l.maxStop,
          maxConnectionTime: l.maxConnectionTime,
          minConnectionTime: l.minConnectionTime,
          disableMultiAirportConnection: l.disableMultiAirportConnection,
          earliestDepartureTime: l.earliestDepartureTime,
          latestDepartureTime: l.latestDepartureTime,
          maxTravelTime: l.maxTravelTime,
          allowedDepartureAirports: l.allowedDepartureAirports,
          allowedArrivalAirports: l.allowedArrivalAirports,
          allowedAirlines: l.allowedAirlines,
        })),
      };
    },
  },
  actions: {
    snapshotFilteringCriteriaKey() {
      this.searchedFilteringCriteriaKey = this.filteringCriteriaKey;
    },
    applyFromUrlQuery(url: string) {
      useStopsFilterStore().applyFromUrlQuery(url);
      useHoursFilterStore().applyFromUrlQuery(url);
      useAirlinesFilterStore().applyFromUrlQuery(url);
      // Duration and airports have no multi-destination UI: a URL must not arm a filter the user cannot see or clear.
      // The live flag, not the committed one: this runs on the URL just applied, before any snapshot, so the
      // committed key would still describe the previous search on a client-side deep-link navigation.
      if (useSearchCriteriaStore().isMultiDestination) {
        useDurationFilterStore().$reset();
        useAirportsFilterStore().$reset();
        return;
      }
      useDurationFilterStore().applyFromUrlQuery(url);
      useAirportsFilterStore().applyFromUrlQuery(url);
    },
    resetFilters() {
      useStopsFilterStore().$reset();
      useHoursFilterStore().$reset();
      useDurationFilterStore().$reset();
      useAirportsFilterStore().$reset();
      useAirlinesFilterStore().$reset();
      useSortOptionsStore().$reset();
      useSearchResultIndexStore().resetSortOptions();
      this.$reset();
    },
  },
});
